/**
 * The small toolkit every admin tab is built from.
 *
 * ─── Why the tabs do not share a store ──────────────────────────────────────
 * The rest of this codebase keeps screen state in a `createStore` and rerenders
 * on push (`account-state.ts` behind the Settings screen, `game-state.ts`
 * behind the settlement). That pattern earns its keep when several unrelated
 * parts of the UI read the same state — the HUD, a panel and the paperdoll all
 * watching one character sheet.
 *
 * Nothing in the admin panel is like that. Twelve tabs, each a list or a form
 * over one endpoint, none of them reading each other. A single admin store
 * would be twelve unrelated slices in one object with a twelve-way branch on
 * every push, and twelve stores would be twelve files of boilerplate around a
 * fetch. So each tab owns its own data as ordinary local variables, fetches on
 * mount and returns its teardown, and this file carries what they all share.
 *
 * ─── Escaping ───────────────────────────────────────────────────────────────
 * Every tab builds HTML from template literals, and nearly every value in them
 * came from a player: usernames, emails, KYC full names, withdrawal payout
 * details, the reason text on a rejected request. `esc` is not decoration. The
 * rule is that any interpolation of server data goes through it, and the few
 * places that deliberately render markup say so on the line above.
 */
import { ApiError } from '../../core/network/api';

/** HTML-escapes a value for interpolation into a template literal. */
export function esc(value: unknown): string {
  if (value === null || value === undefined) return '';
  return String(value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

/**
 * Turns whatever an API rejection carries into one line for the notice bar.
 *
 * `ApiError.message` is the backend's own message where there is one — a
 * class-validator complaint, "Only PENDING withdrawals can be approved", the
 * exact pixel size a favicon was rejected for. Those are the useful ones and
 * they are shown verbatim. A status with no body (a proxy timeout, an offline
 * browser) has nothing worth showing, so it gets named by what it is.
 */
export function errorText(err: unknown): string {
  if (err instanceof ApiError) {
    if (err.status === 0) return 'The server could not be reached.';
    if (err.status === 403) return 'Refused — this account is not an administrator.';
    if (err.status === 503) return err.message || 'Disabled in this build.';
    return err.message || `Request failed (${err.status}).`;
  }
  return err instanceof Error ? err.message : 'Something went wrong.';
}

/** ISO timestamp to something readable, or an em dash when there is none. */
export function fmtDate(value: string | null | undefined): string {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString(undefined, {
    year: 'numeric', month: 'short', day: '2-digit', hour: '2-digit', minute: '2-digit',
  });
}

/** Integer cents to a currency string. The API stores money as cents throughout. */
export function fmtCents(cents: number, currency = 'EUR'): string {
  const amount = (cents ?? 0) / 100;
  try {
    return amount.toLocaleString(undefined, { style: 'currency', currency });
  } catch {
    // An unknown or malformed currency code from the database must not take the
    // whole row down with a RangeError.
    return `${amount.toFixed(2)} ${currency}`;
  }
}

/** Seconds to a compact uptime. */
export function fmtDuration(seconds: number): string {
  const d = Math.floor(seconds / 86_400);
  const h = Math.floor((seconds % 86_400) / 3_600);
  const m = Math.floor((seconds % 3_600) / 60);
  if (d) return `${d}d ${h}h`;
  if (h) return `${h}h ${m}m`;
  return `${m}m`;
}

// ─── Building blocks ─────────────────────────────────────────────────────────

export interface Column<T> {
  label: string;
  /** Returns a cell's INNER HTML — so a renderer that interpolates data escapes it itself. */
  cell: (row: T) => string;
  /** Narrow columns (badges, actions) that should not absorb the free space. */
  tight?: boolean;
  /**
   * An explicit CSS width, for a column that is neither `tight` nor content
   * that should soak up the remainder.
   *
   * `tight` sizes to the content, which is right for a badge and wrong for
   * anything read as a pair of numbers: the balances column packed "531 cash"
   * over "0 gold" into the narrowest box that would hold them, so every row's
   * figures sat at a different width and the column could not be scanned down.
   * A fixed width gives it a column to be read as.
   */
  width?: string;
}

/** The class/style a column contributes to both its `<th>` and its `<td>`s. */
function cellAttrs<T>(column: Column<T>): string {
  const cls = column.tight ? ' class="tg-adm-tight"' : '';
  // `width` on a table cell is a MINIMUM under `table-layout: auto`, which is
  // what is wanted here — the column gets its room and can still grow for an
  // unusually long value rather than clipping it.
  const style = column.width ? ` style="width:${esc(column.width)}"` : '';
  return cls + style;
}

/** A table, or the empty-state line when there is nothing in it. */
export function table<T>(rows: T[], columns: Column<T>[], empty: string): string {
  if (!rows.length) return `<p class="tg-adm-empty">${esc(empty)}</p>`;
  return `
    <div class="tg-adm-tablewrap">
      <table class="tg-adm-table">
        <thead><tr>${columns
          .map((c) => `<th${cellAttrs(c)}>${esc(c.label)}</th>`)
          .join('')}</tr></thead>
        <tbody>${rows
          .map((row) => `<tr>${columns
            .map((c) => `<td${cellAttrs(c)}>${c.cell(row)}</td>`)
            .join('')}</tr>`)
          .join('')}</tbody>
      </table>
    </div>`;
}

/**
 * Page controls for the `{ page, pageSize, total }` envelope.
 *
 * Renders nothing at all for a single page — a lone disabled "1 / 1" under
 * every short list is noise.
 */
export function pager(page: number, pageSize: number, total: number): string {
  const pages = Math.max(1, Math.ceil(total / Math.max(1, pageSize)));
  if (pages <= 1) return '';
  return `
    <div class="tg-adm-pager">
      <button type="button" class="tg-adm-btn" data-page="${page - 1}" ${page <= 1 ? 'disabled' : ''}>Previous</button>
      <span class="tg-adm-pageno">Page ${page} of ${pages} &middot; ${total} total</span>
      <button type="button" class="tg-adm-btn" data-page="${page + 1}" ${page >= pages ? 'disabled' : ''}>Next</button>
    </div>`;
}

/** A coloured status pill. `tone` picks the colour; the text is escaped. */
export function badge(text: string, tone: 'ok' | 'warn' | 'bad' | 'mute' = 'mute'): string {
  return `<span class="tg-adm-badge tg-adm-badge-${tone}">${esc(text)}</span>`;
}

/** A labelled text input, pre-filled and escaped. */
export function field(
  name: string,
  label: string,
  value: unknown = '',
  opts: { type?: string; hint?: string; long?: boolean; placeholder?: string } = {},
): string {
  const control = opts.long
    ? `<textarea name="${esc(name)}" rows="6" placeholder="${esc(opts.placeholder ?? '')}">${esc(value)}</textarea>`
    : `<input name="${esc(name)}" type="${esc(opts.type ?? 'text')}" value="${esc(value)}"
         placeholder="${esc(opts.placeholder ?? '')}">`;
  return `
    <label class="tg-adm-field">
      <span class="tg-adm-label">${esc(label)}</span>
      ${control}
      ${opts.hint ? `<span class="tg-adm-hint">${esc(opts.hint)}</span>` : ''}
    </label>`;
}

/** A labelled checkbox. */
export function checkbox(name: string, label: string, checked: boolean): string {
  return `
    <label class="tg-adm-check">
      <input name="${esc(name)}" type="checkbox" ${checked ? 'checked' : ''}>
      <span>${esc(label)}</span>
    </label>`;
}

// ─── What a tab is ───────────────────────────────────────────────────────────

/**
 * What the shell hands each tab.
 *
 * `notify` is how a tab reports the outcome of a write — it draws in the
 * shell's own notice bar rather than inside the tab body, so a rerender that
 * replaces the body cannot erase the message explaining what just happened.
 * `confirm` is the shell's modal, injected for the same reason
 * `settings-ui.ts` injects its own: it keeps the destructive actions testable
 * as "this goes THROUGH a confirmation" rather than straight to the server.
 */
export interface TabContext {
  notify(message: string, kind?: 'ok' | 'err'): void;
  confirm(opts: { title: string; body: string; confirmLabel: string; onConfirm: () => void }): void;
  /**
   * A confirmation that also collects one line of text.
   *
   * Exists because rejecting a KYC submission or a withdrawal REQUIRES a
   * reason — `@IsString() @Length(1, 1000)`, and for KYC the text is emailed
   * to the player verbatim. A plain confirm would have to post an empty string
   * and get a validation array back, and the alternative of an inline form in
   * every table row loses the "are you sure" on an irreversible action.
   */
  prompt(opts: {
    title: string;
    body: string;
    label: string;
    confirmLabel: string;
    onConfirm: (value: string) => void;
  }): void;
  /** Leaves the panel — used by the "open in game" style links. */
  navigate(path: string): void;
}

export interface AdminTab {
  id: string;
  label: string;
  /** Rail heading this tab sits under. */
  group: string;
  /** Renders into `host`; returns its teardown. */
  mount(host: HTMLElement, ctx: TabContext): () => void;
}

/**
 * The shape every tab's body follows: a heading, an optional toolbar, and the
 * content. Kept here so twelve tabs cannot drift into twelve layouts.
 */
export function section(title: string, subtitle: string, body: string, toolbar = ''): string {
  return `
    <header class="tg-adm-head">
      <div>
        <h2 class="tg-adm-title">${esc(title)}</h2>
        ${subtitle ? `<p class="tg-adm-sub">${esc(subtitle)}</p>` : ''}
      </div>
      ${toolbar ? `<div class="tg-adm-toolbar">${toolbar}</div>` : ''}
    </header>
    <div class="tg-adm-body">${body}</div>`;
}

/**
 * Reads a form into a plain object.
 *
 * Checkboxes come back as booleans rather than the string "on", because every
 * consumer here posts straight to a class-validator `@IsBoolean()` field and
 * "on" fails it with a message nobody would connect to a tickbox.
 */
export function readForm(form: HTMLFormElement): Record<string, string | boolean> {
  const out: Record<string, string | boolean> = {};
  for (const el of Array.from(form.elements)) {
    const input = el as HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement;
    if (!input.name) continue;
    if (input instanceof HTMLInputElement && input.type === 'checkbox') {
      out[input.name] = input.checked;
    } else if (input instanceof HTMLInputElement && input.type === 'file') {
      continue;
    } else {
      out[input.name] = input.value;
    }
  }
  return out;
}
